
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Route } from 'vue-router';
import api from '@/api';
import Util from '@/utils/util';
import Swal from 'sweetalert2';
import UrlWithQrCode from '@/components/UrlWithQrCode.vue';

const { merchant, store } = api;

interface Store {
  storeId: string;
  storeName: string;
  isMainStore: boolean;
}

@Component({
  components: { UrlWithQrCode },
})
export default class StoreList extends Vue {
  @Prop() private merchantId!: number;

  private panel = 0;

  private merchantName: string = '';

  private data: {
    data: Store[];
    totalCount: number;
    pageSize: number;
    pageNo: number;
  } = { data: [], totalCount: 0, pageSize: 20, pageNo: 0 };

  private selected: Store | null = null;

  private loading = false;

  private headers = [
    { text: '名稱', value: 'storeName' },
    { text: '地址', value: 'address' },
    { text: '是否為總店', value: 'isMainStore', width: 150 },
    { text: '付款連結', value: 'paymentUrl', width: 100 },
    { text: '修改', value: 'edit', width: 100 },
    { text: '管理員', value: 'admin', width: 100 },
    { text: '刪除', value: 'delete', width: 100 },
  ];

  private get mainStoreId() {
    const mainStore = this.data.data.filter((s) => s.isMainStore).pop();
    return mainStore?.storeId;
  }

  private get totalCount() {
    return this.data.totalCount;
  }

  private get pageCount() {
    return Math.ceil((this.data.totalCount || 0) / (this.data.pageSize || 20));
  }

  private get collection() {
    return [...(this.data.data || [])];
  }

  private get hyperlink() {
    return this.selected
      ? `${process.env.VUE_APP_WEB}/web/pay/${this.selected.storeId}`
      : null;
  }

  private filter = {
    storeName: this.$route.query.storeName || undefined,
    pageSize: Util.isNullOrUndefined(this.$route.query.pageSize)
      ? 20
      : +this.$route.query.pageSize,
    pageNo: Util.isNullOrUndefined(this.$route.query.pageNo)
      ? 1
      : +this.$route.query.pageNo,
  };

  private async updatePage(page: number) {
    this.filter.pageNo = page;
    await this.query();
  }

  private async query() {
    const query = {
      storeName: this.filter.storeName,
      pageSize: `${this.filter.pageSize}`,
      pageNo: `${this.filter.pageNo}`,
    };
    if (this.isQueryChanged(query)) {
      await this.$router.replace({ query });
    }
  }

  private async changeMainStore(storeId: string) {
    const { status } = await store.mainStore(this.merchantId, storeId);
    if (status) {
      await this.requestData();
    }
  }

  private async deleteStore(storeId: string) {
    const { isConfirmed } = await Swal.fire({
      title: '您確定要刪除嗎?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    });
    if (isConfirmed === false) return;
    const { status } = await store.delete(this.merchantId, storeId);
    if (status) {
      await this.requestData();
    } else {
      await this.requestData();
    }
  }

  private async reset() {
    this.filter = {
      storeName: undefined,
      pageSize: 20,
      pageNo: 1,
    };
    await this.query();
  }

  private isQueryChanged(query: {
    [key: string]: string | Array<string | null> | null | undefined;
  }): boolean {
    return !Object.keys(query).every(
      (key: string) => query[key]?.toString() === this.$route.query[key],
    );
  }

  private async requestData() {
    this.loading = true;
    if (this.merchantName === '') {
      const {
        data: { merchantName },
      } = await merchant.get(this.merchantId);
      this.merchantName = merchantName;
    }
    const { data } = await store.list(this.merchantId, this.filter);
    this.data = data;
    this.loading = false;
  }

  private async beforeRouteEnter(
    to: Route,
    from: Route,
    next: (to?: ((vm: StoreList) => void) | boolean) => void,
  ) {
    await next(async (vm) => {
      await vm.requestData();
    });
  }

  private async beforeRouteUpdate(
    to: Route,
    from: Route,
    next: (to?: ((vm: StoreList) => void) | boolean) => void,
  ) {
    if (to.name === 'storeList') await this.requestData();
    await next(true);
  }
}
